import { haveProperty } from '@/utils/';
/**
 *
 * Construção do array de utilizadores com o valor
 * da associação, que por default é true ( 1 ).
 *
 * @param {Array}  iterationUsers
 * @param {Array}  validationUsers
 * @param {Boolean}  assign - valor default 1
 *
*/
const builderUserAssociation = ({
    iterationUsers,
    validationUsers,
    assign = 1
}) => iterationUsers.filter( id => !validationUsers.includes( id ) ).map( id => ({ id, assign }) );

/**
 *
 * Criação do objecto de campanha.
 * Esta função irá preparar o objecto a ser enviado para API, onde só os campos
 * preenchidos na página é que serão enviados. Assim irá permitir usar esta função
 * na adição e na edição deste modelo.
 *
 * @param {Object}  data
 * @param {Object}  helpers
 *
*/
const create = ( data, helpers ) => {
    // exemplos:
    // -- morada: ...( zipcode && { 'address[zip_code_id]': prepareZipcode(zipcode) } ),
    // -- users: users[`users[${index}][id]`] = id;
    // -- contactCallStates: contact_call_states[`users[${index}][id]`] = id;

    const callStates = {};
    if ( haveProperty( data, 'contactCallState' ) ) {
        const state = helpers.contactCallStates.find( contactCallState => contactCallState.name === data.contactCallState );

        callStates['contact_call_states[0][name]'] = data.contactCallState;
        callStates['contact_call_states[0][description]'] = state?.description || data.contactCallState;
        callStates['contact_call_states[0][billable]'] = data.callPrice || state?.billable || 0;
        callStates['contact_call_states[0][group_id]'] = state?.group?.id || data.group;
        callStates['contact_call_states[0][operation]'] = 'create';
        callStates['contact_call_states[0][notifiable]'] = +data.notify || 0;

        if ( data.numberOfAttempts )
            callStates['contact_call_states[0][resolution][retry_limit]'] = data.numberOfAttempts;

        if ( data?.intervalOfAttempts ) {
            const intervalOfAttemptsArray = data.intervalOfAttempts.split( ', ' );

            intervalOfAttemptsArray.forEach( ( interval, index ) => {
                callStates[`contact_call_states[0][resolution][retry_in][${index}]`] = interval;
            });
        }

        if ( data.numberOfAttempts || data?.intervalOfAttempts )
            callStates['contact_call_states[0][resolution][shared_retry_limit]'] = +data.sharingRules || 0;
    }
    // Se tiver contactCallStateId, significa que se está a tentar apagar um estado
    if ( haveProperty( data, 'contactCallStateId' ) ) {
        callStates['contact_call_states[0][id]'] = data.contactCallStateId;
        callStates['contact_call_states[0][operation]'] = data.contactCallStateOperation;
    }

    // Se existir algum e-mail na página da campanha, secção dos estados da chamadas,
    // vão ser todos adicionados para o objeto 'emails' que será posteriormente
    // adicionado ao objeto principal que será enviado para a API.
    const emails = {};
    if ( haveProperty( data, 'emails' ) ) {
        data.emails.forEach( ( email, index ) => {
            emails[`mailing_list[${index}]`] = email;
        });
    }

    const users = {};
    // Construção do objecto de utilizadores a ser enviado para a API.
    // -- Na primeira linha é obtido os utilizadores removidos pelo utilizador.
    //    Aqui é percorrido os utilizadores provenientes da API e obtém os que diferem
    //    dos utilizadores selecionados visualmente pelo utilizador.
    // -- Na segunda linha é obtido os novos utilizadores selecionados pelo utilizador.
    //    Aqui é percorrido os utilizadores selecionados pelo utilizador e obtém os que diferem dos utilizadores
    //    provenientes da API.
    if ( haveProperty( data, 'users' ) ) {
        [
            ...builderUserAssociation({ iterationUsers: helpers.selectedUsers || [], validationUsers: data.users, assign: 0 }),
            ...builderUserAssociation({ iterationUsers: data.users, validationUsers: helpers.selectedUsers || [] })
        ].forEach( ({ id, assign }, index ) => {
            users[`users[${index}][id]`] = id;
            users[`users[${index}][assign]`] = assign;
        });
    }

    const workflowId = data.workflowId || helpers.workflow.id;

    const campaign = {
        ...( haveProperty( data, 'name' ) && { name: data.name }),
        ...( haveProperty( data, 'state' ) && { state_id: data.state }),
        ...( haveProperty( data, 'clientId' ) && { client_id: data.clientId }),
        ...( haveProperty( data, 'beginDate' ) && { begin_date: data.beginDate }),
        ...( haveProperty( data, 'endDate' ) && { end_date: data.endDate }),
        ...( haveProperty( data, 'workflowId' ) && { workflow_id: data.workflowId }),
        ...( haveProperty( data, 'typeId' ) && { type_id: data.typeId }),
        ...( haveProperty( data, 'ddi' ) && workflowId && parseInt( workflowId, 10 ) === 1 && { outbound_ddi_id: data.ddi }),
        ...( haveProperty( data, 'ddi' ) && workflowId && parseInt( workflowId, 10 ) === 2 && { inbound_ddi_id: data.ddi }),
        ...( Object.keys( users ).length && { ...users }),
        ...( Object.keys( callStates ).length && { ...callStates }),
        ...( Object.keys( emails ).length && { ...emails }),
        ...( haveProperty( data, 'observations' ) && { observations: data.observations }),
        ...( haveProperty( data, 'schedules' ) && {
            schedules: []
        }),
        ...( haveProperty( data, 'removeId' ) && { id: data.removeId }),
        ...( haveProperty( data, 'postCallTimer' ) && { 'settings[post_call_timer]': data.postCallTimer })
    };
    let index = 0;
    if ( haveProperty( data, 'schedules' ) ) {
        data.schedules.forEach( element => {
            if ( element.id )
                campaign[`schedules[${index}][id]`] = element.id;
            if ( element.weekday && element.weekday.length === 1 )
                campaign[`schedules[${index}][weekday]`] = element.weekday;
            if ( element.startsAt )
                campaign[`schedules[${index}][starts_at]`] = element.startsAt;
            if ( element.endsAt )
                campaign[`schedules[${index}][ends_at]`] = element.endsAt;
            campaign[`schedules[${index}][operation]`] = element.operation;
            index++;
        });
    }
    index = 0;

    return campaign;
};

const requiredFields = [
    { field: 'name', isRequiredOnEdit: false },
    { field: 'clientId', isRequiredOnEdit: false },
    { field: 'beginDate', isRequiredOnEdit: false },
    { field: 'endDate', isRequiredOnEdit: false },
    { field: 'typeId', isRequiredOnEdit: false },
    { field: 'workflowId', isRequiredOnEdit: false },
    { field: 'ddi', isRequiredOnEdit: false }
];

export {
    create,
    requiredFields
};